'use client';

import { exposeClassnames as classnames } from '@stadionhq-v3/utilities';
import { Container, Dropdown, NoResultFound } from '@stadionhq-v3/base-ui';
import { useMemo, useState } from 'react';
import { ProfilePositionMapper } from '@stadionhq-v3/core';
import { ProfileListingsProps } from './profileListings.types';
import ProfileCard from '../../molecules/profile-card/profileCard.component';
import styles from './profileListings.module.scss';

export const DEFAULT_FILTER = '*';

export type ProfilePositionFilterOptionsIds = ProfilePositionMapper | '*';

export type ProfilePositionFilterOptions = {
  id: ProfilePositionFilterOptionsIds;
  text: string;
};

const ProfileListings = ({
  title,
  subtitle,
  positions,
  profiles,
  labels: { noResultsFoundTitle, showAll },
  nativeProps,
  classNames,
}: ProfileListingsProps) => {
  const [filter, setFilter] =
    useState<ProfilePositionFilterOptionsIds>(DEFAULT_FILTER);
  const filteredProfiles = useMemo(
    () =>
      profiles.filter(
        (profile) => filter === DEFAULT_FILTER || profile.position === filter
      ),
    [profiles, filter]
  );

  const classes = classnames(
    [styles.tokens, styles.profileListings],
    classNames
  );

  return (
    <Container
      {...nativeProps}
      classNames={[classes]}
      data-component="ProfileListings"
    >
      {(title || subtitle) && (
        <div className={classnames(styles.heading)}>
          {title && <h2 className={classnames(styles.title)}>{title}</h2>}
          {subtitle && (
            <h3 className={classnames(styles.subtitle)}>{subtitle}</h3>
          )}
        </div>
      )}
      {positions && (
        <div className={classnames(styles.positions)}>
          <Dropdown<ProfilePositionFilterOptions>
            classNames={[classnames(styles.positionsFilter)]}
            id="player-positions-filter"
            items={[
              {
                id: DEFAULT_FILTER,
                text: showAll || 'Show all',
              },
              ...positions.map((position) => ({
                id: position,
                text: position,
              })),
            ]}
            onSelection={(selectedFilter) => setFilter(selectedFilter.id)}
          />
        </div>
      )}
      {profiles && (
        <div className={classnames(styles.profiles)}>
          {Boolean(filteredProfiles.length) && (
            <>
              {filteredProfiles.map((profile) => (
                <ProfileCard {...profile} key={profile.id} />
              ))}
            </>
          )}
          {!filteredProfiles.length && (
            <NoResultFound
              classNames={[classnames(styles.noResultsFound)]}
              title={noResultsFoundTitle || 'No results found'}
            />
          )}
        </div>
      )}
    </Container>
  );
};

export default ProfileListings;
